import React, { useState, useEffect } from 'react';
import './CharacterLibrary.css';
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';

function CharacterLibrary({ onNavigate }) {
  const [characters, setCharacters] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState(null);

  useEffect(() => {
    loadCharacters();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCharacters(characters);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = characters.filter((char) =>
        char.filename.toLowerCase().includes(query) ||
        (char.name && char.name.toLowerCase().includes(query))
      );
      setFilteredCharacters(filtered);
    }
  }, [searchQuery, characters]);

  const loadCharacters = async () => {
    try {
      setIsLoading(true);
      const response = await influencerService.getCharacterLibrary();
      if (response.success && response.characters) {
        setCharacters(response.characters);
        setFilteredCharacters(response.characters);
      }
    } catch (error) {
      console.error('Failed to load characters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="library-page">
      <div className="header">
        <h1>Character Library</h1>
        <p className="subtitle">Browse and manage your AI-generated characters</p>
      </div>

      {/* Search & Stats */}
      <div className="library-toolbar">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name or filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="library-stats">
          <i className="fas fa-image"></i>
          <span>{filteredCharacters.length} character{filteredCharacters.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="xbtn xbtn-md xbtn-secondary" onClick={loadCharacters}>
          <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="library-empty">
          <i className="fas fa-spinner fa-spin fa-3x"></i>
          <p>Loading your characters...</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && filteredCharacters.length === 0 && (
        <div className="library-empty">
          <i className="fas fa-image fa-3x"></i>
          <p>{searchQuery ? 'No characters found' : 'No characters yet'}</p>
          <small>{searchQuery ? 'Try a different search term' : 'Generate your first character to get started'}</small>
        </div>
      )}

      {/* Grid */}
      {!isLoading && filteredCharacters.length > 0 && (
        <div className="library-grid">
          {filteredCharacters.map((character, index) => (
            <div
              key={character.filename}
              className="library-card"
              onClick={() => setSelectedCharacter(character)}
            >
              <div className="library-card-image">
                <img
                  src={getInfluencerImageUrl(character.path)}
                  alt={character.name || character.filename}
                  loading="lazy"
                />
                <div className="library-card-overlay">
                  <button
                    className="xbtn xbtn-sm xbtn-secondary card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onNavigate) onNavigate('character-edit', {
                        path: character.path,
                        name: character.name || 'Unnamed',
                        filename: character.filename,
                      });
                    }}
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    className="xbtn xbtn-sm xbtn-secondary card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCharacter(character);
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
              <div className="library-card-info">
                <h3>{character.name || 'Unnamed'}</h3>
                <div className="card-meta">
                  <i className="fas fa-calendar"></i>
                  <span>{formatDate(character.created)}</span>
                </div>
                <div className="card-meta">{formatFileSize(character.size)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedCharacter && (
        <div className="modal-overlay" onClick={() => setSelectedCharacter(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div>
                <h2>{selectedCharacter.name || 'Unnamed Character'}</h2>
                <p className="modal-filename">{selectedCharacter.filename}</p>
              </div>
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setSelectedCharacter(null)}>Close</button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-image">
                <img
                  src={getInfluencerImageUrl(selectedCharacter.path)}
                  alt={selectedCharacter.name || selectedCharacter.filename}
                />
              </div>
              <div className="detail-info">
                <h3 className="detail-section-title">Details</h3>
                <div className="detail-row"><span>Name:</span><strong>{selectedCharacter.name || 'Unnamed'}</strong></div>
                <div className="detail-row"><span>Created:</span><strong>{formatDate(selectedCharacter.created)}</strong></div>
                <div className="detail-row"><span>File Size:</span><strong>{formatFileSize(selectedCharacter.size)}</strong></div>
                <div className="detail-row"><span>Filename:</span><code>{selectedCharacter.filename}</code></div>

                <h3 className="detail-section-title" style={{ marginTop: 20 }}>Actions</h3>
                <div className="detail-actions">
                  <button
                    className="xbtn xbtn-md xbtn-primary"
                    onClick={() => {
                      if (onNavigate) onNavigate('character-edit', {
                        path: selectedCharacter.path,
                        name: selectedCharacter.name || 'Unnamed',
                        filename: selectedCharacter.filename,
                      });
                    }}
                  >
                    <i className="fas fa-edit"></i> Edit Character
                  </button>
                  <button className="xbtn xbtn-md xbtn-secondary" onClick={() => window.open(getInfluencerImageUrl(selectedCharacter.path), '_blank')}>
                    Open Full Size
                  </button>
                  <button className="xbtn xbtn-md xbtn-secondary" onClick={() => {
                    const link = document.createElement('a');
                    link.href = getInfluencerImageUrl(selectedCharacter.path);
                    link.download = selectedCharacter.filename;
                    link.click();
                  }}>
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CharacterLibrary;
